import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../utils/api';

/**
 * Fetch all campaigns with filters
 */
export function useCampaigns({ status, search, page = 1, limit = 20 } = {}) {
  return useQuery({
    queryKey: ['campaigns', { status, search, page, limit }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      const res = await apiRequest(`/campaigns?${params.toString()}`);
      return res;
    }
  });
}

/**
 * Fetch single campaign by ID
 */
export function useCampaign(id) {
  return useQuery({
    queryKey: ['campaigns', id],
    queryFn: async () => {
      const res = await apiRequest(`/campaigns/${id}`);
      return res.data;
    },
    enabled: !!id
  });
}

/**
 * Create new campaign
 */
export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const res = await apiRequest('/campaigns', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    }
  });
}

/**
 * Update existing campaign
 */
export function useUpdateCampaign(id) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const res = await apiRequest(`/campaigns/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
    }
  });
}

/**
 * Selectable values for the targeting dropdowns (member types, family branches,
 * roles, user groups). Served from the data so the editor cannot offer a branch
 * or group that no longer exists.
 */
export function useAudienceFields() {
  return useQuery({
    queryKey: ['campaigns', 'audience-fields'],
    queryFn: async () => {
      const res = await apiRequest('/campaigns/audience-fields');
      return res.data;
    },
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Preview campaign audience.
 *
 * Pass the condition currently in the editor rather than relying on what is
 * saved, so unsaved drafts can preview too. When `id` is set the saved-campaign
 * route is used, which also persists the estimate for the list and stats pages.
 */
export function usePreviewAudience(id) {
  return useMutation({
    mutationFn: async ({ audienceCondition, frequencyCapDays } = {}) => {
      const path = id ? `/campaigns/${id}/preview-audience` : '/campaigns/preview-audience';
      const res = await apiRequest(path, {
        method: 'POST',
        body: JSON.stringify({ audienceCondition, frequencyCapDays })
      });
      return res.data;
    }
  });
}

/**
 * Send campaign
 */
export function useSendCampaign(id) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await apiRequest(`/campaigns/${id}/send`, {
        method: 'POST'
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
    }
  });
}

/**
 * Pause campaign
 */
export function usePauseCampaign(id) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await apiRequest(`/campaigns/${id}/pause`, {
        method: 'POST'
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
    }
  });
}

/**
 * Resume campaign
 */
export function useResumeCampaign(id) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await apiRequest(`/campaigns/${id}/resume`, {
        method: 'POST'
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
    }
  });
}

/**
 * Cancel campaign
 */
export function useCancelCampaign(id) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await apiRequest(`/campaigns/${id}/cancel`, {
        method: 'POST'
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
    }
  });
}

/**
 * Duplicate campaign
 */
export function useDuplicateCampaign(id) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await apiRequest(`/campaigns/${id}/duplicate`, {
        method: 'POST'
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    }
  });
}

/**
 * Get campaign stats
 */
export function useCampaignStats(id) {
  return useQuery({
    queryKey: ['campaigns', id, 'stats'],
    queryFn: async () => {
      const res = await apiRequest(`/campaigns/${id}/stats`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: (data) => {
      // Auto-refresh every 15s if campaign is sending
      return data?.status === 'sending' ? 15000 : false;
    }
  });
}
